import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Copy, Check } from "lucide-react";
import { site } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import Magnetic from "@/components/Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = {
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    budget: "",
    message: "",
    privacy: false,
    website: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [copied, setCopied] = useState(false);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Bitte gib deinen Namen an.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Bitte gib eine gültige E-Mail-Adresse an.";
        if (form.message.trim().length < 10) e.message = "Deine Nachricht sollte mindestens 10 Zeichen haben.";
        if (!form.privacy) e.privacy = "Bitte bestätige die Datenschutzerklärung.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            toast.success("Nachricht gesendet.");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Senden fehlgeschlagen. Bitte versuche es erneut.");
        } finally {
            setSending(false);
        }
    };

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(site.email);
            setCopied(true);
            toast.success("E-Mail-Adresse kopiert.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Kopieren nicht möglich.");
        }
    };

    const inputCls = (k) =>
        `w-full border-b bg-transparent py-3 text-base text-[#F4F4F0] outline-none transition-colors duration-300 placeholder:text-[#A1A1AA]/50 focus:border-[#E63946] ${
            errors[k] ? "border-[#E63946]" : "border-white/15"
        }`;

    return (
        <section id="contact" data-testid="contact-section" className="mx-auto max-w-[1600px] px-5 py-28 sm:px-10 sm:py-40">
            <Reveal>
                <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-[#E63946]">Kontakt</p>
                <h2 className="max-w-5xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-[#F4F4F0] sm:text-6xl lg:text-8xl">
                    Lass uns etwas <span className="text-[#E63946]">erschaffen.</span>
                </h2>
                <p className="mt-6 text-lg font-light text-[#A1A1AA]">
                    Du hast ein Projekt im Kopf? Lass uns sprechen.
                </p>
            </Reveal>

            <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
                <Reveal className="lg:col-span-5" delay={0.1}>
                    <div data-testid="contact-details" className="space-y-8">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Studio</p>
                            <p className="mt-2 font-display text-xl font-bold text-[#F4F4F0]">{site.owner}</p>
                            <p className="text-[#A1A1AA]">{site.studioName}</p>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">E-Mail</p>
                            <button
                                data-testid="contact-email-copy"
                                data-cursor="link"
                                onClick={copyEmail}
                                className="mt-2 flex items-center gap-2 text-lg text-[#F4F4F0] transition-colors duration-300 hover:text-[#E63946]"
                            >
                                {site.email}
                                {copied ? <Check size={16} className="text-[#E63946]" /> : <Copy size={16} className="opacity-50" />}
                            </button>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Telefon</p>
                            <p className="mt-2 text-lg text-[#F4F4F0]">{site.phone}</p>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Adresse</p>
                            <p className="mt-2 text-lg text-[#F4F4F0]">{site.address}</p>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Social</p>
                            <div className="mt-3 flex gap-6">
                                {site.socials.map((s) => (
                                    <a
                                        key={s.label}
                                        data-testid={`contact-social-${s.label.toLowerCase()}`}
                                        data-cursor="link"
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative text-sm uppercase tracking-[0.15em] text-[#F4F4F0]/80 transition-colors duration-300 hover:text-[#F4F4F0]"
                                    >
                                        {s.label}
                                        <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#E63946] transition-[width] duration-300 group-hover:w-full" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal className="lg:col-span-7" delay={0.15}>
                    {sent ? (
                        <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center border border-white/10 p-10 sm:p-16">
                            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#E63946]">Erfolgreich gesendet</p>
                            <h3 className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-[#F4F4F0] sm:text-6xl">
                                Danke.
                            </h3>
                            <p className="mt-4 text-lg text-[#A1A1AA]">Deine Nachricht wurde gesendet. Ich melde mich schnellstmöglich bei dir.</p>
                            <button
                                data-testid="contact-new-inquiry"
                                data-cursor="link"
                                onClick={() => {
                                    setForm(initial);
                                    setSent(false);
                                }}
                                className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F4F0] underline-offset-4 hover:text-[#E63946] hover:underline"
                            >
                                Neue Anfrage stellen
                            </button>
                        </div>
                    ) : (
                        <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-8">
                            <input
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={(e) => set("website", e.target.value)}
                                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                            />
                            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                                <div>
                                    <input
                                        data-testid="contact-input-name"
                                        placeholder="Name *"
                                        value={form.name}
                                        onChange={(e) => set("name", e.target.value)}
                                        className={inputCls("name")}
                                    />
                                    {errors.name && <p data-testid="error-name" className="mt-2 text-xs text-[#E63946]">{errors.name}</p>}
                                </div>
                                <div>
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        placeholder="E-Mail *"
                                        value={form.email}
                                        onChange={(e) => set("email", e.target.value)}
                                        className={inputCls("email")}
                                    />
                                    {errors.email && <p data-testid="error-email" className="mt-2 text-xs text-[#E63946]">{errors.email}</p>}
                                </div>
                                <input
                                    data-testid="contact-input-phone"
                                    type="tel"
                                    placeholder="Telefonnummer"
                                    value={form.phone}
                                    onChange={(e) => set("phone", e.target.value)}
                                    className={inputCls("phone")}
                                />
                                <input
                                    data-testid="contact-input-company"
                                    placeholder="Unternehmen"
                                    value={form.company}
                                    onChange={(e) => set("company", e.target.value)}
                                    className={inputCls("company")}
                                />
                            </div>

                            <div>
                                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Art des Projekts</p>
                                <div className="flex flex-wrap gap-2" data-testid="contact-project-type-pills">
                                    {site.projectTypes.map((t) => (
                                        <button
                                            type="button"
                                            key={t}
                                            data-testid={`pill-type-${t.toLowerCase()}`}
                                            data-cursor="link"
                                            onClick={() => set("project_type", form.project_type === t ? "" : t)}
                                            className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.1em] transition-colors duration-300 ${
                                                form.project_type === t
                                                    ? "border-[#E63946] bg-[#E63946] text-[#F4F4F0]"
                                                    : "border-white/15 text-[#A1A1AA] hover:border-white/40 hover:text-[#F4F4F0]"
                                            }`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Budget</p>
                                <div className="flex flex-wrap gap-2" data-testid="contact-budget-pills">
                                    {site.budgets.map((b) => (
                                        <button
                                            type="button"
                                            key={b}
                                            data-testid={`pill-budget-${b.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                                            data-cursor="link"
                                            onClick={() => set("budget", form.budget === b ? "" : b)}
                                            className={`rounded-full border px-4 py-2 text-xs tracking-[0.05em] transition-colors duration-300 ${
                                                form.budget === b
                                                    ? "border-[#E63946] bg-[#E63946] text-[#F4F4F0]"
                                                    : "border-white/15 text-[#A1A1AA] hover:border-white/40 hover:text-[#F4F4F0]"
                                            }`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <textarea
                                    data-testid="contact-input-message"
                                    placeholder="Deine Nachricht *"
                                    rows={4}
                                    value={form.message}
                                    onChange={(e) => set("message", e.target.value)}
                                    className={`${inputCls("message")} resize-none`}
                                />
                                {errors.message && <p data-testid="error-message" className="mt-2 text-xs text-[#E63946]">{errors.message}</p>}
                            </div>

                            <div>
                                <label data-testid="contact-privacy-label" className="flex items-start gap-3 text-sm text-[#A1A1AA]" data-cursor="link">
                                    <input
                                        data-testid="contact-input-privacy"
                                        type="checkbox"
                                        checked={form.privacy}
                                        onChange={(e) => set("privacy", e.target.checked)}
                                        className="mt-1 h-4 w-4 shrink-0 appearance-none border border-white/30 transition-colors duration-200 checked:border-[#E63946] checked:bg-[#E63946]"
                                    />
                                    <span>
                                        Ich habe die{" "}
                                        <a href="/datenschutz" data-cursor="link" className="text-[#F4F4F0] underline underline-offset-4 hover:text-[#E63946]">
                                            Datenschutzerklärung
                                        </a>{" "}
                                        gelesen und bin mit der Verarbeitung meiner Daten zur Bearbeitung der Anfrage einverstanden. *
                                    </span>
                                </label>
                                {errors.privacy && <p data-testid="error-privacy" className="mt-2 text-xs text-[#E63946]">{errors.privacy}</p>}
                            </div>

                            <Magnetic>
                                <button
                                    data-testid="contact-submit-button"
                                    data-cursor="link"
                                    type="submit"
                                    disabled={sending}
                                    className="group flex items-center gap-3 rounded-full border border-[#F4F4F0]/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F4F0] transition-colors duration-300 hover:border-[#E63946] hover:bg-[#E63946] disabled:opacity-50"
                                >
                                    {sending ? "Wird gesendet …" : "Anfrage senden"}
                                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </Magnetic>
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
}
